import { useEffect, useState } from "react";
import useAuth from "../context/AuthProvider";
import React from "react";
import { AgedPayablesReportModel_Resp, AgedPayablesReportModel } from "../models/AgedPayablesReportModel";
import { Box } from "@mui/system";
import { Button, Checkbox, Chip, FormControl, Grid, LinearProgress, TextField, Typography } from "@mui/material";
import { AgedPayablesReportList } from "../components/Lists/AgedPayablesReportList";
import { writeXlsxFile } from "../helper/utils";
import { Search, Close, FileDownload, Refresh } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import formatPrice from "../helper/currencyFormatter";
import { apiClient } from "../helper/api";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";

interface CustomerOption { partnersId: number; customerName: string }
interface StateOption { stateId: number; stateName: string }

/** Sentinel "Select All" row rendered as the first option in each multi-select dropdown — clicking it
 * toggles between selecting every option and clearing the selection. Kept out of the actual value/state
 * (stripped in onChange) so it never appears as a chip or gets sent to the API. IDs/strings chosen so
 * they can't collide with real data (no real Partner/State has a negative id; tax/status values are
 * plain names, never these sentinel strings). */
const SELECT_ALL_CUSTOMER: CustomerOption = { partnersId: -1, customerName: 'Select All' };
const SELECT_ALL_STATE: StateOption = { stateId: -1, stateName: 'Select All' };
const SELECT_ALL_TAX = '__SELECT_ALL_TAX__';
const SELECT_ALL_STATUS = '__SELECT_ALL_STATUS__';

export const AgedPayablesReport = () => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);
    const [reportModuleCL, setReportModuleCL] = useState<AgedPayablesReportModel_Resp>({ getData_Resp: [], totalCount: 0 });
    const [_pageSize, setPageSize] = useState(50);
    const [_currentPage, setCurrentPage] = useState(1);
    const customerData = React.useRef<number[]>([]);
    const stateData = React.useRef<number[]>([]);
    const taxData = React.useRef<string[]>([]);
    const statusData = React.useRef<string[]>([]);
    const invoiceDateStartData = React.useRef<Dayjs | null>(null);
    const invoiceDateEndData = React.useRef<Dayjs | null>(null);
    const dateFiledStartData = React.useRef<Dayjs | null>(null);
    const dateFiledEndData = React.useRef<Dayjs | null>(null);
    const [filterModel, setFilterModel] = useState<{ items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }>({ items: [], logicOperator: 'and' });
    const [sortModel, setSortModel] = useState<{ field: string, sort: string }[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<CustomerOption[]>([]);
    const [selectedStates, setSelectedStates] = useState<StateOption[]>([]);
    const [selectedTaxTypes, setSelectedTaxTypes] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [TaxandFees, setTaxandFees] = useState<string[]>([]);
    const [invoiceDateStart, setInvoiceDateStart] = useState<Dayjs | null>(null);
    const [invoiceDateEnd, setInvoiceDateEnd] = useState<Dayjs | null>(null);
    const [dateFiledStart, setDateFiledStart] = useState<Dayjs | null>(null);
    const [dateFiledEnd, setDateFiledEnd] = useState<Dayjs | null>(null);

    useEffect(() => {
        fetchStateData();
        fetchCustomer();
        fetchTaxFeeName();
    }, []);

    useEffect(() => {
        fetchMainData();
        // Only pagination/sort/grid-filterModel changes auto-refetch — the Client/Home State/Tax Type/
        // Status/date-range filters follow the existing Reconciliation Queue pattern (Search-button
        // driven, via refs) so selecting doesn't fire a request on every change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_pageSize, _currentPage, filterModel, sortModel]);

    const fetchMainData = async (pageOverride?: number) => {
        setLoading(true);
        try {
            const response = await fetchAgedPayablesData(_pageSize, pageOverride ?? _currentPage);
            if (response.resp === 0) {
                setReportModuleCL(response.data);
            } else {
                setReportModuleCL({ getData_Resp: [], totalCount: 0 });
            }
        } catch (error) {
            setReportModuleCL({ getData_Resp: [], totalCount: 0 });
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAgedPayablesData = async (pageSize: number, pageNumber: number) => {
        const requestBody: any = {
            userId: user?.userId || 0,
            customerIds: customerData.current,
            stateIds: stateData.current,
            taxNames: taxData.current,
            statuses: statusData.current,
            pageNumber: pageNumber,
            pageSize: pageSize,
            field: sortModel?.[0]?.['field'] || '',
            sort: sortModel?.[0]?.['sort'] || '',
            filterModel: filterModel,
        };
        if (invoiceDateStartData.current) {
            requestBody.invoiceDateStart = invoiceDateStartData.current.format('YYYY-MM-DD');
        }
        if (invoiceDateEndData.current) {
            requestBody.invoiceDateEnd = invoiceDateEndData.current.format('YYYY-MM-DD');
        }
        if (dateFiledStartData.current) {
            requestBody.dateFiledStart = dateFiledStartData.current.format('YYYY-MM-DD');
        }
        if (dateFiledEndData.current) {
            requestBody.dateFiledEnd = dateFiledEndData.current.format('YYYY-MM-DD');
        }

        return apiClient(`/AgedPayablesReport/getAgedPayablesReportData`, {
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify(requestBody)
        });
    };

    const onExportXlsx = (values: AgedPayablesReportModel[]) => {
        setLoading(true);
        if (values?.length === 0) {
            setLoading(false);
            return;
        }
        const fieldsToExport: (keyof AgedPayablesReportModel)[] = [
            'client', 'invoiceNumber', 'insuredName', 'policyNumber',
            'homeState', 'taxType', 'transactionType', 'invDate',
            'policyEffDate', 'policyExpDate', 'grossPremium', 'taxAmt', 'dateFiled', 'status',
        ];
        const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

        const formattedValues = values.map(item => {
            const cleanAndFormatValue = (value: string | null | undefined): string => {
                if (value == null || value === '') return "";

                let cleanedValue = value.replace(/[,\$]/g, "").trim();

                if (cleanedValue.startsWith("(") && cleanedValue.endsWith(")")) {
                    cleanedValue = "-" + cleanedValue.slice(1, -1);
                }

                const numericValue = parseFloat(cleanedValue);
                return isNaN(numericValue) ? value : `${formatPrice(numericValue.toString())}`;
            };

            item.taxAmt = cleanAndFormatValue(item.taxAmt);
            item.grossPremium = cleanAndFormatValue(item.grossPremium);

            const invDate = item.invDate && item.invDate !== '1900-01-01T00:00:00' ? new Date(item.invDate) : "";
            const policyEffDate = item.policyEffDate && item.policyEffDate !== '1900-01-01T00:00:00' ? new Date(item.policyEffDate) : "";
            const policyExpDate = item.policyExpDate && item.policyExpDate !== '1900-01-01T00:00:00' ? new Date(item.policyExpDate) : "";
            const dateFiled = item.dateFiled && item.dateFiled !== '1900-01-01T00:00:00' ? new Date(item.dateFiled) : "";

            const update: (string | number | Date | undefined)[] = fieldsToExport.map((key: keyof AgedPayablesReportModel) => {
                if (key === 'invDate') return invDate;
                if (key === 'policyEffDate') return policyEffDate;
                if (key === 'policyExpDate') return policyExpDate;
                if (key === 'dateFiled') return dateFiled;
                return item[key] as string | number | undefined;
            });

            return update;
        });

        const headers = fieldsToExport.map(field => capitalizeFirstLetter(field));

        const updates = [
            headers,
            ...formattedValues
        ];
        writeXlsxFile(updates, "QualityControlReport");
        setLoading(false);
    };

    const handleExportXlsxNew = async () => {
        try {
            setLoading(true);
            const response = await fetchAgedPayablesData(reportModuleCL.totalCount, 1);
            if (!response) return;
            if (response.resp === 0) {
                const val = response.data.getData_Resp;
                if (!val || val.length === 0) return;
                onExportXlsx(val);
            }
        } catch (error) {
            console.error("Error occurred:", error);
        }
        setLoading(false);
    };

    // NOTE: this deliberately calls TaxesAndFees/GetAllTaxandFeeName (distinct TaxName values from the
    // TaxesAndFees table), NOT PolicyFilingType/GetAllTaxandFeeName — the latter returns PolicyFilingType
    // names, a different table with no guaranteed overlap with tx.TaxName, which is the column this report
    // actually filters on (getAgedPayablesReportData.sql). ReconciliationQueue.tsx uses the wrong (former)
    // endpoint for its own Tax Type filter — not copied here.
    const fetchTaxFeeName = () => {
        apiClient(`/TaxesAndFees/GetAllTaxandFeeName`, {
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({ StateId: 0 }),
        }).then((responseJson) => {
            if (!responseJson) return
            if (responseJson.data != null) {
                setTaxandFees(responseJson.data);
            }
        })
    };

    // "Removed" is deliberately excluded: this report's base query is always open-only
    // (PolicyStatusId <> 7, i.e. not Removed), so a Removed row can never appear here — offering it as a
    // filter option would silently return zero rows with no explanation.
    const statusList = [
        'Not Started',
        'Waiting on Client',
        'Ready to File',
        'Filed',
        'Filed - Binder'
    ];

    const [Customer, setCustomer] = useState<CustomerOption[]>([]);
    const fetchCustomer = () => {
        apiClient("/Partners/getPartners", {
            method: "GET",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((responseJson) => {
                if (!responseJson) return
                setCustomer(responseJson.data);
            })
    };

    const [States, setStates] = useState<StateOption[]>([]);
    const fetchStateData = () => {
        apiClient("/States/getStates", {
            method: "GET",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((responseJson) => {
                if (!responseJson) return
                setStates(responseJson.data);
            })
    };

    function ClearSearch() {
        setLoading(true);
        stateData.current = [];
        setSelectedStates([]);
        customerData.current = [];
        setSelectedCustomers([]);
        taxData.current = [];
        setSelectedTaxTypes([]);
        statusData.current = [];
        setSelectedStatuses([]);
        invoiceDateStartData.current = null;
        invoiceDateEndData.current = null;
        dateFiledStartData.current = null;
        dateFiledEndData.current = null;
        setInvoiceDateStart(null);
        setInvoiceDateEnd(null);
        setDateFiledStart(null);
        setDateFiledEnd(null);
        setLoading(false);
        // Reset to page 1 explicitly (not just via setCurrentPage, whose effect won't fire a refetch if
        // already on page 1) so clearing filters while on page 2+ doesn't request a RowNum range past the
        // new, smaller result set and render a false "no rows" grid.
        setCurrentPage(1);
        fetchMainData(1);
    }

    const datePickerSx = {
        width: '230px',
        '& .MuiOutlinedInput-root': {
            height: '36px',
            '& fieldset': { borderColor: '#e2e8f0', borderRadius: '6px' },
            '&:hover fieldset': { borderColor: '#2099d2' },
            '&.Mui-focused fieldset': { borderColor: '#2099d2', borderWidth: '1px' },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#2099d2' },
    };

    const multiSelectSx = {
        marginTop: "-4px !important",
        "& .MuiAutocomplete-input": { padding: "0px 4px 0px 5px !important", minWidth: "0px !important" },
        // Force the chip + "+N" summary onto a single line regardless of label length — MUI's own
        // .MuiAutocomplete-inputRoot defaults to flex-wrap, which is what let a long tax/client name
        // push the "+N" indicator onto its own row.
        "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap", overflow: "hidden" },
    };

    const multiSelectInputSx = {
        '& .MuiInputBase-root': { marginTop: '5px !important' },
        '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
        '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
        '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ width: "100%" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        backgroundColor: "#fafbfc",
                        minHeight: 56,
                        marginLeft: { xs: '-24px', md: '0px' },
                        marginRight: { xs: '-24px', md: '0px' },
                    }}
                >
                    <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: "#0f172a" }}>
                        Quality Control Report
                    </h4>
                </Box>
                <Box sx={{ width: "100%", py: 2 }}>
                    <Grid container spacing={2} className="filter-grid" display="flex">
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    sx={multiSelectSx}
                                    options={[SELECT_ALL_CUSTOMER, ...Customer]}
                                    getOptionLabel={(option) => option.customerName}
                                    isOptionEqualToValue={(option, value) => option.partnersId === value.partnersId}
                                    renderTags={(value, getTagProps) => (
                                        <>
                                            {value.slice(0, 1).map((option, index) => (
                                                <Chip {...getTagProps({ index })} key={option.partnersId} label={option.customerName} size="small" sx={{ maxWidth: "65%", flexShrink: 1 }} />
                                            ))}
                                            {value.length > 1 && (
                                                <span style={{ marginLeft: 4, fontSize: "13px", color: "#475569", flexShrink: 0, whiteSpace: "nowrap" }}>{`+${value.length - 1}`}</span>
                                            )}
                                        </>
                                    )}
                                    onChange={(event, value) => {
                                        if (value.some((v) => v.partnersId === SELECT_ALL_CUSTOMER.partnersId)) {
                                            const nextValue = selectedCustomers.length === Customer.length ? [] : Customer;
                                            setSelectedCustomers(nextValue);
                                            customerData.current = nextValue.map((v) => v.partnersId);
                                            return;
                                        }
                                        setSelectedCustomers(value);
                                        customerData.current = value.map((v) => v.partnersId);
                                    }}
                                    value={selectedCustomers}
                                    renderOption={(props, option, { selected }) => {
                                        if (option.partnersId === SELECT_ALL_CUSTOMER.partnersId) {
                                            const allSelected = Customer.length > 0 && selectedCustomers.length === Customer.length;
                                            return (
                                                <li {...props} style={{ fontWeight: 600 }}>
                                                    <Checkbox checked={allSelected} indeterminate={!allSelected && selectedCustomers.length > 0} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                    Select All
                                                </li>
                                            );
                                        }
                                        return (
                                            <li {...props}>
                                                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                {option.customerName}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={{
                                                ".MuiFormLabel-root.Mui-focused": { color: "#000" },
                                                ...multiSelectInputSx,
                                            }}
                                            {...params}
                                            label="Client"
                                            placeholder={selectedCustomers.length ? undefined : "All Clients"}
                                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    sx={multiSelectSx}
                                    options={[SELECT_ALL_STATE, ...States]}
                                    getOptionLabel={(option) => option.stateName}
                                    isOptionEqualToValue={(option, value) => option.stateId === value.stateId}
                                    renderTags={(value, getTagProps) => (
                                        <>
                                            {value.slice(0, 1).map((option, index) => (
                                                <Chip {...getTagProps({ index })} key={option.stateId} label={option.stateName} size="small" sx={{ maxWidth: "65%", flexShrink: 1 }} />
                                            ))}
                                            {value.length > 1 && (
                                                <span style={{ marginLeft: 4, fontSize: "13px", color: "#475569", flexShrink: 0, whiteSpace: "nowrap" }}>{`+${value.length - 1}`}</span>
                                            )}
                                        </>
                                    )}
                                    onChange={(event, value) => {
                                        if (value.some((v) => v.stateId === SELECT_ALL_STATE.stateId)) {
                                            const nextValue = selectedStates.length === States.length ? [] : States;
                                            setSelectedStates(nextValue);
                                            stateData.current = nextValue.map((v) => v.stateId);
                                            return;
                                        }
                                        setSelectedStates(value);
                                        stateData.current = value.map((v) => v.stateId);
                                    }}
                                    value={selectedStates}
                                    renderOption={(props, option, { selected }) => {
                                        if (option.stateId === SELECT_ALL_STATE.stateId) {
                                            const allSelected = States.length > 0 && selectedStates.length === States.length;
                                            return (
                                                <li {...props} style={{ fontWeight: 600 }}>
                                                    <Checkbox checked={allSelected} indeterminate={!allSelected && selectedStates.length > 0} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                    Select All
                                                </li>
                                            );
                                        }
                                        return (
                                            <li {...props}>
                                                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                {option.stateName}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={{
                                                ".MuiFormLabel-root.Mui-focused": { color: "#000" },
                                                ...multiSelectInputSx,
                                            }}
                                            {...params}
                                            label="Home State"
                                            placeholder={selectedStates.length ? undefined : "All States"}
                                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    sx={multiSelectSx}
                                    options={[SELECT_ALL_TAX, ...TaxandFees]}
                                    renderTags={(value, getTagProps) => (
                                        <>
                                            {value.slice(0, 1).map((option, index) => (
                                                <Chip {...getTagProps({ index })} key={option} label={option} size="small" sx={{ maxWidth: "65%", flexShrink: 1 }} />
                                            ))}
                                            {value.length > 1 && (
                                                <span style={{ marginLeft: 4, fontSize: "13px", color: "#475569", flexShrink: 0, whiteSpace: "nowrap" }}>{`+${value.length - 1}`}</span>
                                            )}
                                        </>
                                    )}
                                    onChange={(event, value) => {
                                        if (value.includes(SELECT_ALL_TAX)) {
                                            const nextValue = selectedTaxTypes.length === TaxandFees.length ? [] : TaxandFees;
                                            setSelectedTaxTypes(nextValue);
                                            taxData.current = nextValue;
                                            return;
                                        }
                                        setSelectedTaxTypes(value);
                                        taxData.current = value;
                                    }}
                                    value={selectedTaxTypes}
                                    renderOption={(props, option, { selected }) => {
                                        if (option === SELECT_ALL_TAX) {
                                            const allSelected = TaxandFees.length > 0 && selectedTaxTypes.length === TaxandFees.length;
                                            return (
                                                <li {...props} style={{ fontWeight: 600 }}>
                                                    <Checkbox checked={allSelected} indeterminate={!allSelected && selectedTaxTypes.length > 0} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                    Select All
                                                </li>
                                            );
                                        }
                                        return (
                                            <li {...props}>
                                                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                {option}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={{
                                                ".MuiFormLabel-root.Mui-focused": { color: "#000" },
                                                ...multiSelectInputSx,
                                            }}
                                            {...params}
                                            label="Tax Type"
                                            placeholder={selectedTaxTypes.length ? undefined : "All Tax Types"}
                                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    sx={multiSelectSx}
                                    options={[SELECT_ALL_STATUS, ...statusList]}
                                    renderTags={(value, getTagProps) => (
                                        <>
                                            {value.slice(0, 1).map((option, index) => (
                                                <Chip {...getTagProps({ index })} key={option} label={option} size="small" sx={{ maxWidth: "65%", flexShrink: 1 }} />
                                            ))}
                                            {value.length > 1 && (
                                                <span style={{ marginLeft: 4, fontSize: "13px", color: "#475569", flexShrink: 0, whiteSpace: "nowrap" }}>{`+${value.length - 1}`}</span>
                                            )}
                                        </>
                                    )}
                                    onChange={(event, value) => {
                                        if (value.includes(SELECT_ALL_STATUS)) {
                                            const nextValue = selectedStatuses.length === statusList.length ? [] : statusList;
                                            setSelectedStatuses(nextValue);
                                            statusData.current = nextValue;
                                            return;
                                        }
                                        setSelectedStatuses(value);
                                        statusData.current = value;
                                    }}
                                    value={selectedStatuses}
                                    renderOption={(props, option, { selected }) => {
                                        if (option === SELECT_ALL_STATUS) {
                                            const allSelected = selectedStatuses.length === statusList.length;
                                            return (
                                                <li {...props} style={{ fontWeight: 600 }}>
                                                    <Checkbox checked={allSelected} indeterminate={!allSelected && selectedStatuses.length > 0} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                    Select All
                                                </li>
                                            );
                                        }
                                        return (
                                            <li {...props}>
                                                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                                                {option}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={{
                                                ".MuiFormLabel-root.Mui-focused": { color: "#000" },
                                                ...multiSelectInputSx,
                                            }}
                                            {...params}
                                            label="Status"
                                            placeholder={selectedStatuses.length ? undefined : "All Statuses"}
                                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <Box sx={{ display: "flex", gap: 2, width: "fit-content", flexWrap: "wrap" }}>
                                <FormControl sx={{ flex: "0 0 auto" }}>
                                    <DatePicker
                                        label="Start Date for Invoice Date"
                                        value={invoiceDateStart}
                                        onChange={(newValue) => {
                                            setInvoiceDateStart(newValue as Dayjs | null);
                                            invoiceDateStartData.current = newValue as Dayjs | null;
                                        }}
                                        inputFormat="MM/DD/YYYY"
                                        maxDate={invoiceDateEnd || undefined}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Start Date for Invoice Date" size="small" sx={datePickerSx} />
                                        )}
                                    />
                                </FormControl>
                                <FormControl sx={{ flex: "0 0 auto" }}>
                                    <DatePicker
                                        label="End Date for Invoice Date"
                                        value={invoiceDateEnd}
                                        onChange={(newValue) => {
                                            setInvoiceDateEnd(newValue as Dayjs | null);
                                            invoiceDateEndData.current = newValue as Dayjs | null;
                                        }}
                                        inputFormat="MM/DD/YYYY"
                                        minDate={invoiceDateStart || undefined}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="End Date for Invoice Date" size="small" sx={datePickerSx} />
                                        )}
                                    />
                                </FormControl>
                                <FormControl sx={{ flex: "0 0 auto" }}>
                                    <DatePicker
                                        label="Start Date for Date Filed"
                                        value={dateFiledStart}
                                        onChange={(newValue) => {
                                            setDateFiledStart(newValue as Dayjs | null);
                                            dateFiledStartData.current = newValue as Dayjs | null;
                                        }}
                                        inputFormat="MM/DD/YYYY"
                                        maxDate={dateFiledEnd || undefined}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Start Date for Date Filed" size="small" sx={datePickerSx} />
                                        )}
                                    />
                                </FormControl>
                                <FormControl sx={{ flex: "0 0 auto" }}>
                                    <DatePicker
                                        label="End Date for Date Filed"
                                        value={dateFiledEnd}
                                        onChange={(newValue) => {
                                            setDateFiledEnd(newValue as Dayjs | null);
                                            dateFiledEndData.current = newValue as Dayjs | null;
                                        }}
                                        inputFormat="MM/DD/YYYY"
                                        minDate={dateFiledStart || undefined}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="End Date for Date Filed" size="small" sx={datePickerSx} />
                                        )}
                                    />
                                </FormControl>
                            </Box>
                        </Grid>
                        {/* Filter action buttons — their own grouped container (Box), but as one item
                            in the SAME Grid as the inputs above, so it sits on the same row when there's
                            room and only wraps to its own line when the viewport is too narrow. */}
                        <Grid item xs={12} sm="auto">
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={() => { setCurrentPage(1); fetchMainData(1); }}
                                    startIcon={<Search />}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        backgroundColor: "#2099d2",
                                        "&:hover": { backgroundColor: "#176990" },
                                    }}
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={() => { ClearSearch(); }}
                                    startIcon={<Close />}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        "&:hover": { borderColor: "#2099d2", color: "#2099d2" },
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={() => window.location.reload()}
                                    startIcon={<Refresh />}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        "&:hover": {
                                            borderColor: "#2099d2",
                                            backgroundColor: "#2099d214",
                                            color: "#2099d2",
                                        },
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', position: 'relative' }}>
                {loading && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            backgroundColor: "rgba(250, 251, 252, 0.85)",
                            zIndex: 10,
                            border: "1px solid #e2e8f0",
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ width: "100%", maxWidth: 320 }}>
                            <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                            <Box sx={{ mt: 1.5, textAlign: "center" }}>
                                <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                            </Box>
                        </Box>
                    </Box>
                )}
                <div className="d-flex align-items-center justify-content-between py-3 px-2">
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#334155', mr: 'auto', pl: 1 }}>Quality Control Report</Typography>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => { handleExportXlsxNew(); }}
                            startIcon={<FileDownload />}
                            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2099d2", "&:hover": { backgroundColor: "#176990" } }}
                        >
                            Export
                        </Button>
                    </div>
                </div>

                <AgedPayablesReportList
                    ReportModuleCL={reportModuleCL}
                    setCurrentPage={setCurrentPage}
                    setPageSize={setPageSize}
                    _pageSize={_pageSize}
                    filterModel={filterModel}
                    setFilterModel={setFilterModel}
                    setSortModel={setSortModel}
                    sortModel={sortModel}
                    loading={loading}
                />
            </Box>
        </LocalizationProvider>
    );
};
